import { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react";
import { storageGet, storageSet } from "@/shared/utils/storage";
import { BUILT_IN_THEMES } from "@/shared/styles/theme";
import type { ThemeDefinition } from "@/shared/styles/theme";
import {
  DEFAULT_THEME_ID,
  THEME_COOKIE_KEY,
  THEME_COOKIE_MAX_AGE_SECONDS,
  THEME_STORAGE_KEY,
} from "@/shared/styles/themePreference";

interface ThemeContextValue {
  activeThemeId: string;
  setActiveThemeId: (id: string) => void;
  availableThemes: ThemeDefinition[];
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useThemeContext(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useThemeContext must be used within a ThemeContextProvider");
  }
  return ctx;
}

export default function ThemeContextProvider({
  initialThemeId = DEFAULT_THEME_ID,
  children,
}: Readonly<{ initialThemeId?: string; children: React.ReactNode }>) {
  const [activeThemeId, setActiveThemeId] = useState<string>(() => {
    if (globalThis.window === undefined) return initialThemeId;
    return storageGet<string>(THEME_STORAGE_KEY) ?? initialThemeId;
  });

  const persistThemeCookie = useCallback((id: string) => {
    document.cookie = `${THEME_COOKIE_KEY}=${encodeURIComponent(id)}; Path=/; Max-Age=${String(THEME_COOKIE_MAX_AGE_SECONDS)}; SameSite=Lax`;
  }, []);

  const persistActiveThemeId = useCallback(
    (id: string) => {
      setActiveThemeId(id);
      storageSet(THEME_STORAGE_KEY, id);
      persistThemeCookie(id);
    },
    [persistThemeCookie],
  );

  useEffect(() => {
    storageSet(THEME_STORAGE_KEY, activeThemeId);
    persistThemeCookie(activeThemeId);
  }, [activeThemeId, persistThemeCookie]);

  // Resolve the effective theme ID: if the persisted ID no longer matches
  // any available theme (e.g. custom theme removed), fall back to "light".
  // Computed synchronously during render — no effect needed.
  const effectiveThemeId = BUILT_IN_THEMES.some((t) => t.id === activeThemeId)
    ? activeThemeId
    : DEFAULT_THEME_ID;

  const value = useMemo<ThemeContextValue>(
    () => ({
      activeThemeId: effectiveThemeId,
      setActiveThemeId: persistActiveThemeId,
      availableThemes: BUILT_IN_THEMES,
    }),
    [effectiveThemeId, persistActiveThemeId],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
